// views/admin/AdminListView.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminListApi, deleteAdminApi } from '@/lib/api';
import { useLoading } from '@/store/loadingStore';
import { useModal } from '@/store/modalStore';
import { AdminResponseType, PageResponse } from '@/types';
import './AdminListView.css';

const AdminListView = () => {
    const navigate = useNavigate();
    const { startLoading, stopLoading } = useLoading();
    const modal = useModal();

    // 상태
    const [activeTab, setActiveTab] = useState('관리자정보 등록');
    const [admins, setAdmins] = useState<AdminResponseType[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const pageSize = 10;

    // 관리자 목록 조회
    const fetchAdmins = async (page: number) => {
        startLoading('관리자 목록을 불러오는 중...');
        try {
            const response = await getAdminListApi(page, pageSize);
            setAdmins(response.content);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
            setCurrentPage(page);
        } catch (error) {
            modal.alert('오류', '관리자 목록을 불러오는데 실패했습니다.');
        } finally {
            stopLoading();
        }
    };

    // 컴포넌트 마운트 시 데이터 로드
    useEffect(() => {
        fetchAdmins(0);
    }, []);

    // 탭 변경
    const handleTabChange = (tab: string) => {
        if (tab === '공지사항') {
            navigate('/notice');
            return;
        }
        setActiveTab(tab);
        // 다른 탭 기능은 추후 구현
    };

    // 신규등록
    const handleRegister = () => {
        navigate('/admin/register');
    };

    // 정보수정
    const handleEdit = (adminId: number) => {
        navigate(`/admin/${adminId}/edit`);
    };

    // 삭제
    const handleDelete = (admin: AdminResponseType) => {
        modal.confirmDelete(
            `${admin.name}님의 계정을 삭제하시겠습니까?`,
            async () => {
                try {
                    await deleteAdminApi(admin.adminId);
                    modal.alert('삭제 완료', '관리자가 삭제되었습니다.');
                    fetchAdmins(currentPage);
                } catch (error) {
                    modal.alert('삭제 실패', '삭제 중 오류가 발생했습니다.');
                }
            }
        );
    };

    // 페이지 변경
    const handlePageChange = (page: number) => {
        if (page >= 0 && page < totalPages) {
            fetchAdmins(page);
        }
    };

    // 페이지 번호 생성
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(0, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    const handleExcelDownload = () => {
        console.log("엑셀 다운로드");
        // 실제 구현 시 downloadAdminExcelApi() 호출
    };

    return (
        <div className="device-list-container">
            {/* 탭 버튼 - StatView와 동일한 구조 */}
            <div className="tab-buttons">
                <div className="" style={{ float: "left" }}>
                    <button
                        className={`tab-button ${activeTab === '공지사항' ? 'active' : ''}`}
                        onClick={() => handleTabChange('공지사항')}
                    >
                        공지사항
                    </button>
                    <button
                        className={`tab-button ${activeTab === '관리자정보 등록' ? 'active' : ''}`}
                        onClick={() => handleTabChange('관리자정보 등록')}
                    >
                        관리자정보 등록
                    </button>
                    <button
                        className={`tab-button ${activeTab === '로고 등록' ? 'active' : ''}`}
                        onClick={() => handleTabChange('로고 등록')}
                    >
                        로고 등록
                    </button>
                </div>
                <a
                    href={"javascript:;"}
                    className="btn-excel-download"
                    onClick={handleExcelDownload}
                    style={{ float: "right" }}
                >
                    <img src={"/img/btn_excel01.png"} />
                </a>
            </div>

            {/* dlv_dv01 박스 - StatView와 동일한 구조 */}
            <div className="dlv_dv01">
                <div className="env-content">
                    {/* 헤더 영역 */}
                    <div className="content-header">
                        <h1 className="page-title">운영자 목록</h1>
                        <button className="btn-register" onClick={handleRegister}>
                            신규등록
                        </button>
                    </div>

                    {/* 테이블 섹션 */}
                    <div className="table-section">
                        <table className="device-table">
                            <thead>
                            <tr>
                                <th style={{ width: '60px' }}>번호</th>
                                <th style={{ width: '100px' }}>이름</th>
                                <th style={{ width: '120px' }}>아이디</th>
                                <th style={{ width: '140px' }}>연락처</th>
                                <th style={{ width: '100px' }}>부서</th>
                                <th style={{ width: '80px' }}>직급</th>
                                <th style={{ width: '120px' }}>담당업무</th>
                                <th style={{ width: '140px' }}>운영등급</th>
                                <th style={{ width: '150px' }}>작업</th>
                            </tr>
                            </thead>
                            <tbody>
                            {admins.length > 0 ? (
                                admins.map((admin, index) => (
                                    <tr key={admin.adminId}>
                                        <td className="row-number">
                                            {String(totalElements - (currentPage * pageSize) - index).padStart(2, '0')}
                                        </td>
                                        <td>{admin.name}</td>
                                        <td>{admin.userId}</td>
                                        <td>{admin.contact || '-'}</td>
                                        <td>{admin.department || '-'}</td>
                                        <td>{admin.position || '-'}</td>
                                        <td>{admin.workScope || '-'}</td>
                                        <td>{admin.adminGrade}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                className="btn-edit"
                                                onClick={() => handleEdit(admin.adminId)}
                                            >
                                                정보수정
                                            </button>
                                            <button
                                                className="btn-delete"
                                                onClick={() => handleDelete(admin)}
                                            >
                                                삭제
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                                        등록된 관리자가 없습니다.
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이지네이션 */}
                    {totalPages > 0 && (
                        <div className="pagination">
                            <button
                                className={`page-btn ${currentPage === 0 ? 'disabled' : ''}`}
                                onClick={() => handlePageChange(0)}
                                disabled={currentPage === 0}
                            >
                                «
                            </button>
                            <button
                                className={`page-btn ${currentPage === 0 ? 'disabled' : ''}`}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 0}
                            >
                                ‹
                            </button>

                            {getPageNumbers().map(page => (
                                <button
                                    key={page}
                                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page + 1}
                                </button>
                            ))}

                            <button
                                className={`page-btn ${currentPage === totalPages - 1 ? 'disabled' : ''}`}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages - 1}
                            >
                                ›
                            </button>
                            <button
                                className={`page-btn ${currentPage === totalPages - 1 ? 'disabled' : ''}`}
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={currentPage === totalPages - 1}
                            >
                                »
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminListView;